<template>
  <div class="major-media-page-styled">
    <!-- 1. Hero Section -->
    <section class="hero-section-major">
      <div class="container hero-content-major">
        <div class="hero-text-major">
          <h1>{{ $t('majorMediaPage.hero.title') }}</h1>
          <p class="subtitle">{{ $t('majorMediaPage.hero.subtitle') }}</p>
        </div>
        <div class="hero-image-major">
          <img :src="heroIllustration" :alt="$t('majorMediaPage.hero.imageAlt')" />
        </div>
      </div>
    </section>

    <!-- 2. Platforms that support account opening -->
    <section class="supported-platforms-section section-padding">
      <div class="container">
        <h2 class="section-title text-center">{{ $t('majorMediaPage.supportedPlatforms.title') }}</h2>
        <div class="platforms-grid">
          <div v-for="platform in supportedPlatforms" :key="platform.key" class="platform-card">
            <img :src="platform.logo" :alt="platform.name" class="platform-logo-icon" />
            <span>{{ platform.name }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Full life cycle integrated marketing -->
    <section class="lifecycle-marketing-section section-padding">
      <div class="container">
        <h2 class="section-title text-center">{{ $t('majorMediaPage.lifecycleMarketing.title') }}</h2>
        <div class="lifecycle-cards-grid">
          <div v-for="item in lifecycleItems" :key="item.key" class="lifecycle-card">
            <img :src="item.icon" :alt="$t(item.textKey)" class="lifecycle-icon" />
            <p>{{ $t(item.textKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. Customer Case -->
    <section class="customer-cases-section section-padding">
      <div class="container">
        <h2 class="section-title text-center">{{ $t('majorMediaPage.customerCases.title') }}</h2>
        <div class="cases-grid">
          <div v-for="caseItem in customerCases" :key="caseItem.channel" class="case-card">
            <div class="case-header">
              <h3>{{ caseItem.channel }}</h3>
            </div>
            <div class="case-body">
              <div class="case-details">
                <ul>
                  <li v-if="caseItem.deliveryData !== undefined && caseItem.deliveryData !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.deliveryData') }}</span>
                    <span class="detail-value">{{ caseItem.deliveryData }}</span>
                  </li>
                  <li v-if="caseItem.cpi !== undefined && caseItem.cpi !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.cpi') }}</span>
                    <span class="detail-value">{{ caseItem.cpi.replace(/^CPi:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.cvr !== undefined && caseItem.cvr !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.cvr') }}</span>
                    <span class="detail-value">{{ caseItem.cvr.replace(/^CVR:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.ctr !== undefined && caseItem.ctr !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.ctr') }}</span>
                    <span class="detail-value">{{ caseItem.ctr.replace(/^CTR:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.costPerInstall !== undefined && caseItem.costPerInstall !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.costPerInstall') }}</span>
                    <span class="detail-value">{{ caseItem.costPerInstall.replace(/^Cost per Install:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.install !== undefined && caseItem.install !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.install') }}</span>
                    <span class="detail-value">{{ caseItem.install.replace(/^Install:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.roi !== undefined && caseItem.roi !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.roi') }}</span>
                    <span class="detail-value">{{ caseItem.roi.replace(/^ROI:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.countryRegion !== undefined && caseItem.countryRegion !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.countryRegion') }}</span>
                    <span class="detail-value">{{ caseItem.countryRegion.replace(/^Country\/Region:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.platform !== undefined && caseItem.platform !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.platform') }}</span>
                    <span class="detail-value">{{ caseItem.platform.replace(/^Platform:\s*/i, '') }}</span>
                  </li>
                  <li v-if="caseItem.language !== undefined && caseItem.language !== ''">
                    <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.language') }}</span>
                    <span class="detail-value">{{ caseItem.language.replace(/^Language:\s*/i, '') }}</span>
                  </li>
                </ul>
                <div v-if="caseItem.link" class="case-link-wrapper">
                  <span class="detail-label">{{ $t('majorMediaPage.customerCases.labels.link') }}</span>
                  <span class="detail-value case-link-text">{{ caseItem.link }}</span>
                </div>
              </div>
              <div class="case-preview-image">
                <img :src="caseItem.previewImage" :alt="caseItem.channel + ' preview'" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
// 导入图片资源 - 确保文件存在于指定路径
// Hero Section
import heroIllustrationPath from '@/assets/major-media-styled/开户.png';

// Supported Platforms Logos
import facebookLogoPath from '@/assets/major-media-styled/fb.png';
import googleLogoPath from '@/assets/major-media-styled/google.png';
import tiktokLogoPath from '@/assets/major-media-styled/tt.png';
import xLogoPath from '@/assets/major-media-styled/x.png';
import kwaiLogoPath from '@/assets/major-media-styled/Kwai.png';

// Lifecycle Marketing Icons
import oneClickIconPath from '@/assets/major-media-styled/One-click-account-opening.png';
import agencyPlacementIconPath from '@/assets/major-media-styled/icon-agency.png';
import creativeMaterial1IconPath from '@/assets/major-media-styled/icon-creative1.png';
import creativeMaterial2IconPath from '@/assets/major-media-styled/icon-creative2.png';
import growthAnalysisIconPath from '@/assets/major-media-styled/icon-growth.png';

// Customer Cases Preview Images
import shareitPreviewPath from '@/assets/major-media-styled/case-shareit.png';
import noizzPreviewPath from '@/assets/major-media-styled/case-noizz.png';
import xvpnPreviewPath from '@/assets/major-media-styled/case-xvpn.png';
import newborntownPreviewPath from '@/assets/major-media-styled/case-newborntown.png';


export default {
  name: 'MajorMediaPageStyled',
  data() {
    return {
      heroIllustration: heroIllustrationPath,
      supportedPlatforms: [
        { key: 'facebook', name: 'Facebook', logo: facebookLogoPath },
        { key: 'google', name: 'Google', logo: googleLogoPath },
        { key: 'tiktok', name: 'TikTok', logo: tiktokLogoPath },
        { key: 'x', name: 'X', logo: xLogoPath },
        { key: 'kwai', name: 'Kwai', logo: kwaiLogoPath },
      ],
      lifecycleItems: [
        { key: 'oneClick', icon: oneClickIconPath, textKey: 'majorMediaPage.lifecycleMarketing.items.oneClick' },
        { key: 'agency', icon: agencyPlacementIconPath, textKey: 'majorMediaPage.lifecycleMarketing.items.agency' },
        { key: 'creative1', icon: creativeMaterial1IconPath, textKey: 'majorMediaPage.lifecycleMarketing.items.creative1' },
        { key: 'creative2', icon: creativeMaterial2IconPath, textKey: 'majorMediaPage.lifecycleMarketing.items.creative2' },
        { key: 'growth', icon: growthAnalysisIconPath, textKey: 'majorMediaPage.lifecycleMarketing.items.growth' },
      ],
      customerCases: [
        {
          channel: 'Channel—SHAREit',
          deliveryData: '',
          cpi: 'CPi: $0.02',
          costPerInstall: 'Cost per Install: 1529 M',
          countryRegion: 'Country/Region: Whole Area',
          platform: 'Platform: Google/Facebook',
          language: 'Language: English, Arabic, Spanish, etc.',
          link: 'https://play.google.com/store/apps/details?id=com.lenovo.anyshare.gps&hl=en_SG',
          previewImage: shareitPreviewPath,
        },
        {
          channel: 'Channel—Noizz',
          deliveryData: '',
          cpi: 'CPi: $0.02',
          cvr: 'CVR: 55%',
          countryRegion: 'Country/Region: IN (India)',
          platform: 'Platform: Google/Facebook',
          language: 'Language: English Hindi',
          link: 'https://play.google.com/store/apps/details?id=com.yy.biu&hl=zh&gl=US',
          previewImage: noizzPreviewPath,
        },
        {
          channel: 'Channel—XVPN',
          deliveryData: '',
          cpi: 'CPi: $0.02',
          ctr: 'CTR: 3.54%',
          countryRegion: 'Country/Region: US UK',
          platform: 'Platform: Google/Facebook/TikTok',
          language: 'Language: English',
          link: 'https://apps.apple.com/us/app/x-vpn-super-vpn-best-proxy/id1250312807',
          previewImage: xvpnPreviewPath,
        },
        {
          channel: 'Channel—NewBornTown',
          deliveryData: '',
          install: 'Install: 6M',
          roi: 'ROI: 130%',
          countryRegion: 'Country/Region: IN (India)',
          platform: 'Platform: TikTok/Facebook',
          language: 'Language: English Hindi',
          link: 'https://play.google.com/store/apps/details?id=com.missgirl.live',
          previewImage: newborntownPreviewPath,
        },
      ],
    };
  }
};
</script>

<style scoped>
/* 基本样式和辅助类 */
.major-media-page-styled {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  color: #1a202c;
  background-image: url('~@/assets/major-media-styled/bj.jpg');
  background-size: cover; 
  background-position: center; 
  background-repeat: no-repeat; 
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-padding {
  padding: 80px 0;
}
@media (max-width: 768px) {
  .section-padding {
    padding: 60px 0;
  }
}

.platform-card,
.lifecycle-card,
.case-card {
  background-color: #ffffff;
}

.text-center {
  text-align: center;
}

/* --- 标题字体和颜色统一修改 --- */
.section-title,
.hero-text-major h1 {
  font-family: "Acumin Variable Concept", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  font-weight: 700;
  color: #1a202c; /* 统一设置为黑色或非常深的灰色 */
}

/* 区块标题的字号和间距 */
.section-title {
  font-size: 2.25rem;
  margin-bottom: 48px;
}

/* 英雄区主标题的特定字号和间距 */
.hero-text-major h1 {
  font-size: 3.75rem;
  line-height: 1.2;
  margin-bottom: 24px;
}


@media (max-width: 768px) {
  .section-title {
    font-size: 1.875rem;
    margin-bottom: 32px;
  }
  .hero-text-major h1 {
    font-size: 2.5rem;
  }
}

/* 1. Hero Section */
.hero-section-major {
  padding: 80px 0;
  overflow: hidden;
}
.hero-content-major {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}
.hero-text-major {
  flex: 1;
  max-width: 550px;
}
.hero-text-major .subtitle {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  font-size: 1.1rem;
  color: #4a5c7a;
  line-height: 1.6;
}
.hero-image-major {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 500px;
}
.hero-image-major img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}
@media (max-width: 992px) {
  .hero-text-major h1 {
    font-size: 3rem;
  }
}
@media (max-width: 768px) {
  .hero-content-major {
    flex-direction: column;
    text-align: center;
  }
  .hero-image-major {
    margin-top: 32px;
    max-width: 350px;
  }
}

/* 2. Supported Platforms Section - 卡片大小统一 */
.supported-platforms-section .platforms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
  justify-content: center;
}

.platform-card {
  background-color: #ffffff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  box-shadow: 0 6px 10px -2px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  aspect-ratio: 4 / 3; /* 尝试使用宽高比统一大小 */
  /* min-height: 160px; */ /* 或者使用最小高度 */
}
.platform-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 20px -3px rgba(0, 0, 0, 0.1), 0 8px 10px -3px rgba(0, 0, 0, 0.05);
}

.platform-logo-icon {
  height: 56px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 8px;
}

.platform-card span {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.4;
}


/* 3. Lifecycle Marketing Section */
.lifecycle-marketing-section .lifecycle-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}
.lifecycle-card {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.lifecycle-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
.lifecycle-icon {
  height: 56px;
  width: auto;
  margin-bottom: 16px;
  object-fit: contain;
}
.lifecycle-card p {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

/* 4. Customer Cases Section - Styles updated for target design */
.customer-cases-section .cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 32px;
}
.case-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.case-header {
  background-color: #4A90E2;
  padding: 12px 24px;
  border-radius: 12px 12px 0 0;
}
.case-header h3 {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  color: #fff;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}
.case-body {
  padding: 24px;
  display: flex;
  gap: 24px;
  flex-grow: 1;
}
.case-details {
  flex: 3;
}
.case-details ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.case-details li,
.case-link-wrapper {
  display: flex;
  align-items: flex-start;
  font-size: 0.95rem;
  color: #2d3748;
  margin-bottom: 10px;
  line-height: 1.6;
  font-weight: 500; /* 案例详情文本加粗 */
}
.case-details li::before,
.case-link-wrapper::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 10px;
  background-color: #4A90E2;
  border-radius: 2px;
  margin-right: 12px;
  margin-top: 6px;
  flex-shrink: 0;
}

.detail-label {
  color: #1a202c;
  margin-right: 8px;
  white-space: nowrap;
}
.detail-value {
  color: #4a5568;
  word-break: break-word;
}
.case-link-wrapper {
  /* Inherits styles from .case-details li */
}
.case-link-text {
  color: #4a5568; /* 与 detail-value 颜色一致 */
  cursor: default;
}

.case-preview-image {
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 10px;
  align-self: flex-start;
}
.case-preview-image img {
  max-width: 100%;
  max-height: 250px;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
}

@media (max-width: 992px) {
  .customer-cases-section .cases-grid {
    grid-template-columns: 1fr;
  }
  .hero-text-major h1 {
    font-size: 3rem;
  }
}
@media (max-width: 768px) {
  .hero-text-major h1 {
    font-size: 2.5rem;
  }
   .platform-card {
    aspect-ratio: 3 / 2;
  }
}
@media (max-width: 576px) {
  .case-body {
    flex-direction: column;
  }
  .case-preview-image {
    margin-top: 20px;
    align-self: center;
    max-width: 280px;
  }
  .case-details {
    flex: auto;
  }
   .case-details li, .case-link-wrapper {
    font-size: 0.9rem;
    margin-bottom: 8px;
   }
   .case-details li::before, .case-link-wrapper::before {
     margin-top: 5px;
   }
   .supported-platforms-section .platforms-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
  }
  .platform-card {
    padding: 16px;
    gap: 12px;
    aspect-ratio: 1 / 1;
  }
  .platform-logo-icon {
    height: 40px;
  }
  .platform-card span {
    font-size: 1rem;
  }
}
</style>